import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useVectorModal } from '../../../hooks/useVectorMemoryModal';
import { useNotificationStore } from '../../../store/storeNotifications';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
  type DialogProps
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridRowParams,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import { FileText, Plus, Trash, Warning, X } from '@phosphor-icons/react';

import { BULK_MAX_DISPLAY_ITEMS } from '.';
import { spacesQueries } from '../../../queries/spaces';
import { vectorQueries } from '../../../queries/vector';
import {
  VectorService,
  type VectorAddDimensionsRequest,
  type VectorDeleteDimensionsRequest,
} from '../../../service/VectorService';

import { dimensionSchema } from './DimensionSettingsAddDimensionForm';
import type { VectorFileData } from './vectorModal';

interface VectorDimensionBulkEditModalProps extends DialogProps {
  onOpenChange: (open?: boolean) => void;
}

type DimensionData = z.infer<typeof dimensionSchema>;

function VectorDimensionBulkEditModal({
  open,
  onOpenChange,
  ...props
}: VectorDimensionBulkEditModalProps) {
  const queryClient = useQueryClient();

  const setNotification = useNotificationStore(state => state.setNotification);

  const {
    tab,
    name,
    namespaceId,
    spaceId,
    embeddingModel,

    stagingFiles,
    selectedStagingFiles,
    onStagingFilesChange,

    storedFiles,
    selectedStoredFiles,

    namespaceDimensions,
    onNamespaceDimensionsChange,

    isEditing,
  } = useVectorModal();

  const {
    register,
    // control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      isDefault: false,
    },
    resolver: zodResolver(dimensionSchema),
  });

  const [selectedDimension, setSelectedDimension] = useState('');

  const columns: Array<GridColDef> = [
    {
      field: 'title',
      headerName: 'Dimension Name',
      editable: false,
      width: 300,
      renderCell: (param: GridRenderCellParams<any, string>) => {
        const isDimensionPresentOnEveryFile = (
          allFiles as VectorFileData[]
        ).every(file =>
          file.dimensions.find(dimension => dimension.id === param.row.id),
        );

        return (
          <Box
            component="span"
            sx={{
              width: '100%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {param.value}

            {!isDimensionPresentOnEveryFile ? (
              <Tooltip
                title="This dimension does not exist in all documents"
                placement="top"
              >
                <Warning weight="fill" color="#ff9103" size={16} />
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'description',
      editable: false,
      headerName: 'Dimension Description',
      width: 300,
      renderCell: (param: GridRenderCellParams<any, Array<string>>) => (
        <span>{param.value}</span>
      ),
    },
    // {
    //   field: 'isDefault',
    //   type: 'actions',
    //   width: 200,
    //   renderHeader: () => (
    //     <span style={{ fontWeight: 500 }}>Apply to new files</span>
    //   ),
    //   getActions: (params: GridRowParams) => [
    //     <Switch
    //       checked={params.row.isDefault}
    //       // onChange={() => handleAssignAsDefaultChange(params.row)}
    //     />,
    //   ],
    // },
    {
      field: 'delete',
      type: 'actions',
      width: 38,
      getActions: (params: GridRowParams) => [
        <Tooltip
          key="dimension-setting-tooltip"
          title="This will permanently delete this dimension from all the chosen files"
          placement="top"
        >
          <GridActionsCellItem
            sx={{
              padding: '0.5rem',
              borderRadius: 0,
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: 'transparent',

              transition: 'all 150ms ease-in',

              '&:hover': {
                backgroundColor: '#ffebee',
                color: 'red',
                borderColor: 'red',
              },

              '&>svg': {
                transition: 'all 150ms ease-in',
              },
            }}
            icon={<Trash size={16} />}
            label="Delete Dimension"
            onClick={() =>
              handleDimensionDelete(params.row.id, params.row.title)
            }
          />
        </Tooltip>,
      ],
    },
  ];

  const allFiles = useMemo(() => {
    if (tab === 'edit-stored-files') {
      return storedFiles.filter(({ id }) => selectedStoredFiles.includes(id));
    }

    return stagingFiles.filter(({ id }) => selectedStagingFiles.includes(id));
  }, [
    tab,
    storedFiles,
    selectedStoredFiles,
    stagingFiles,
    selectedStagingFiles,
  ]);

  const visibleFiles =
    allFiles.length > BULK_MAX_DISPLAY_ITEMS
      ? allFiles.slice(0, BULK_MAX_DISPLAY_ITEMS)
      : allFiles;
  const hiddenFiles =
    visibleFiles.length < allFiles.length
      ? allFiles.slice(BULK_MAX_DISPLAY_ITEMS)
      : null;

  const filesIds = allFiles.map(file => file.id);

  const seen = new Set();
  const dimensions = allFiles
    .flatMap(item => item.dimensions)
    .filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });

  const handleDimensionCreate = (data: DimensionData) => {
    const dimensionId = crypto.randomUUID();

    const dimensionToAdd = {
      description: data.description,
      isDefault: data.isDefault,
      title: data.title,
    };

    reset();

    if (isEditing) {
      addDimensionsFn({
        body: {
          dimensions: [dimensionToAdd],
          embeddingModelName: embeddingModel,
          filesIds,
        },
        params: { nameSpace: name, id: namespaceId, spaceId },
      });

      return;
    }

    const newFileList = stagingFiles.map(file => {
      if (filesIds.includes(file.id)) {
        return {
          ...file,
          dimensions: [
            ...file.dimensions,
            { id: dimensionId, ...dimensionToAdd },
          ],
        };
      }

      return file;
    });

    onStagingFilesChange(newFileList);

    const newDimensionList = [
      ...namespaceDimensions,
      { id: dimensionId, ...dimensionToAdd },
    ];

    onNamespaceDimensionsChange(newDimensionList);
  };

  const handleDimensionAdd = (id: string) => {
    console.log('Bulk Editor - handleDimensionAdd called with id:', id);
    console.log('Bulk Editor - isEditing:', isEditing);
    console.log('Bulk Editor - namespaceDimensions:', namespaceDimensions);
    
    if (isEditing) {
      addDimensionsFn({
        body: {
          dimensions: [{ id }],
          embeddingModelName: embeddingModel,
          filesIds,
        },
        params: { nameSpace: name, id: namespaceId, spaceId },
      });

      setSelectedDimension('');
      return;
    }

    const dimensionToAdd = namespaceDimensions.find(
      dimension => dimension.id === id,
    );

    console.log('Bulk Editor - dimensionToAdd:', dimensionToAdd);

    if (dimensionToAdd) {
      const newFileList = stagingFiles.map(file => {
        if (filesIds.includes(file.id)) {
          const hasDimension = file.dimensions.find(
            dimension => dimension.id === id,
          );

          if (hasDimension) {
            return file;
          }

          return {
            ...file,
            dimensions: [...file.dimensions, dimensionToAdd],
          };
        }

        return file;
      });

      onStagingFilesChange(newFileList);
      setSelectedDimension('');
    }
  };

  const handleDimensionDelete = (id: string, title: string) => {
    if (isEditing) {
      deleteDimensionsFn({
        body: {
          dimensions: [{ id, title }],
          embeddingModelName: embeddingModel,
          filesIds,
        },
        params: { nameSpace: name, id: namespaceId, spaceId },
      });

      return;
    }

    const newFileList = stagingFiles.map(file => {
      if (filesIds.includes(file.id)) {
        const newDimensionList = file.dimensions.filter(
          dimension => dimension.id !== id,
        );

        return {
          ...file,
          dimensions: newDimensionList,
        };
      }

      return file;
    });

    onStagingFilesChange(newFileList);
  };

  const { mutate: addDimensionsFn } = useMutation({
    mutationFn: ({ body, params }: VectorAddDimensionsRequest) =>
      VectorService.addDimensions({ body, params }),
    onMutate: variables => {
      setNotification({
        type: 'warning',
        message: `Adding dimension ${variables.body.dimensions[0].title}`,
      });
    },
    onError: (_, variables) => {
      setNotification({
        type: 'error',
        message: `Failed to add dimension ${variables.body.dimensions[0].title}`,
      });
    },
    onSuccess: (_, variables) => {
      if (spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      setNotification({
        type: 'success',
        message: `Successfully added dimension ${variables.body.dimensions[0].title}`,
      });

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  const { mutate: deleteDimensionsFn } = useMutation({
    mutationFn: ({ body, params }: VectorDeleteDimensionsRequest) =>
      VectorService.deleteDimensions({ body, params }),
    onMutate: variables => {
      setNotification({
        type: 'warning',
        message: `Deleting dimension ${variables.body.dimensions[0].title}`,
      });
    },
    onError: (_, variables) => {
      setNotification({
        type: 'error',
        message: `Failed to delete dimension ${variables.body.dimensions[0].title}`,
      });
    },
    onSuccess: (_, variables) => {
      if (spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      setNotification({
        type: 'success',
        message: `Successfully deleted dimension ${variables.body.dimensions[0].title}`,
      });

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  return (
    <Dialog
      open={open}
      fullWidth
      sx={{
        '.MuiPaper-root': {
          minWidth: '1000px',
          maxWidth: '1000px',
          borderRadius: '1rem',

          '&>*+*': {
            marginBlockStart: '1.5rem',
          },
        },
      }}
      {...props}
    >
      <header style={{ paddingBlockStart: '2rem', paddingInline: '2rem' }}>
        <DialogTitle sx={{ paddingInline: 0 }} fontSize="1.25rem">
          Manage Dimensions for selected files
        </DialogTitle>

        <List
          sx={{
            paddingInline: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          {visibleFiles.map(({ id, fileName }) => (
            <ListItem
              key={id}
              sx={{
                padding: '0.5rem',

                width: 'auto',
                height: '100%',

                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',

                backgroundColor: '#fafafa',

                borderStyle: 'solid',
                borderWidth: '1px',
                borderColor: '#d4d4d4',
                borderRadius: '0.5rem',
              }}
            >
              <FileText size={24} />
              {fileName}
            </ListItem>
          ))}

          {hiddenFiles ? (
            <Tooltip
              title={
                <>
                  {hiddenFiles.map(({ id, fileName }) => (
                    <p key={id}>{fileName}</p>
                  ))}
                </>
              }
            >
              <p
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                }}
              >
                + {hiddenFiles.length} more
              </p>
            </Tooltip>
          ) : null}
        </List>
      </header>

      <div style={{ paddingInline: '2rem' }}>
        <Divider />
      </div>

      <DialogContent
        sx={{
          paddingBlock: 0,
          paddingInline: '2rem',

          overflow: 'visible',

          '&>*+*': {
            marginBlockStart: '1.5rem !important',
          },
        }}
      >
        <Paper
          sx={{
            minWidth: 'auto !important',
            maxWidth: 'auto !important',

            boxShadow: 'none',
          }}
        >
          <DataGrid
            // rows={PLACEHOLDER}
            rows={dimensions}
            columns={columns}
            getRowId={row => row.id}
            slotProps={{
              cell: {
                style: {
                  textTransform: 'capitalize',
                },
              },
            }}
            onProcessRowUpdateError={error => {
              console.error(error);
            }}
          />
        </Paper>

        <Box sx={{ '&>*+*': { marginBlockStart: '0.5rem' } }}>
          <Box sx={{ '&>*+*': { marginBlockStart: '0.5rem !important' } }}>
            <Typography variant="h5" fontSize="1rem" fontWeight={700}>
              Add an already created dimension
            </Typography>

            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="dimension-bulk" sx={{ color: '#9e9e9e' }}>
                Pick an already existing dimension
              </InputLabel>

              <Select
                id="dimension-select-bulk"
                fullWidth
                sx={{
                  marginBottom: 0,
                }}
                label="Pick an already existing dimension"
                labelId="dimension-bulk"
                value={selectedDimension}
                onChange={evt => {
                  const value = evt.target.value as string;
                  handleDimensionAdd(value);
                }}
              >
                {namespaceDimensions?.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="#9e9e9e">
                      There are no existing dimensions
                    </Typography>
                  </MenuItem>
                ) : (
                  namespaceDimensions?.map(({ id, title }) => {
                    const isDisabled = (allFiles as VectorFileData[]).every(
                      file =>
                        file.dimensions.find(
                          dimension => dimension.id === id,
                        ),
                    );

                    return (
                      <MenuItem key={id} value={id} disabled={isDisabled}>
                        {title}
                      </MenuItem>
                    );
                  })
                )}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ position: 'relative', isolation: 'isolate' }}>
            <Typography
              sx={{
                marginInline: 'auto',
                width: 'min-content',
                paddingInline: '0.5rem',
                backgroundColor: '#fff',
              }}
              color="#71717a"
            >
              or
            </Typography>
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                zIndex: -1,
                transform: 'translateY(-50%)',
                top: '50%',
                left: 0,
                right: 0,
                backgroundColor: '#e4e4e7',
                height: 1,
              }}
            />
          </Box>

          <Box sx={{ '&>*+*': { marginBlockStart: '0.5rem !important' } }}>
            <Typography variant="h5" fontSize="1rem" fontWeight={700}>
              Add a new dimension
            </Typography>

            <Box
              component="form"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
              onSubmit={handleSubmit(handleDimensionCreate)}
            >
              <TextField
                placeholder="Type dimension name"
                sx={{ flex: 1 }}
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />

              <TextField
                placeholder="Type dimension description"
                sx={{ flex: 1 }}
                error={!!errors.description}
                helperText={errors.description?.message}
                {...register('description')}
              />

              {/*<Controller
                control={control}
                name="isDefault"
                render={({ field: { value, onChange } }) => (
                  <Stack direction="row" alignItems="center">
                    <Switch checked={value} onChange={onChange} />
                    <Typography>Assign as default</Typography>
                  </Stack>
                )}
              />*/}

              <Button type="submit" variant="outlined">
                Add
                <Plus size={16} />
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderBlockStartWidth: '1px',
          borderBlockStartStyle: 'solid',
          borderBlockStartColor: '#EAECF0',

          paddingInline: '2rem',
          paddingBlock: '1rem',
        }}
      >
        <Button variant="contained" onClick={() => onOpenChange(false)}>
          Save Changes
        </Button>
      </DialogActions>

      <Button
        sx={{
          position: 'absolute',
          right: '1.25rem',
          top: '1.25rem',
          padding: '0.25rem',
          minWidth: 'auto',
          zIndex: '10',
          margin: '0 !important',
        }}
        onClick={() => onOpenChange(false)}
      >
        <span
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            borderWidth: 0,
          }}
        >
          Close
        </span>

        <X width={24} height={24} />
      </Button>
    </Dialog>
  );
}

/*
function transformDimensionsData(
	files: Array<VectorFileData | NamespaceFile>
) {
	const dimensionMap = new Map()

	files.forEach(file => {
		file.dimensions.forEach(dimension => {
			const dimensionTitle = dimension.title
			const dimensionDescription = dimension.description

			if (!dimensionMap.has(dimensionTitle)) {
				dimensionMap.set(dimensionTitle, {
					title: dimensionTitle,
					values: [],
					presentOn: []
				});
			}

			const dimensionData= dimensionMap.get(dimensionTitle)

			if (!metadataData.presentOn.includes(file.id)) {
				metadataData.presentOn.push(file.id)
			}

			if (!metadataData.values.includes(metadataValue)) {
				metadataData.values.push(metadataValue)
			}
		})
	})

	return Array.from(metadataMap.values());
}
*/

export { VectorDimensionBulkEditModal };
